import { useEffect, useState } from "react";
import { api } from "@/lib/api";

/** The phase catalog, fetched once per page load and shared.
 *
 *  Several screens need it at the same moment -- the week list to name the
 *  phases on each entry, the day sheet to draw the chips -- and they mount
 *  together. Without a shared promise each one issues its own request for a
 *  list that changes perhaps twice a year, which on a phone in a driveway is
 *  three round trips where one would do.
 *
 *  Cached for the life of the page, not longer: the office can rename a phase,
 *  and a reload is a reasonable price for picking that up.
 */
let cached = null;

export function loadPhases() {
    if (!cached) {
        cached = api.get("/phases").then((r) => r.data).catch((e) => {
            cached = null;          // let the next caller retry rather than
            throw e;                // caching a failure for the whole session
        });
    }
    return cached;
}

export function usePhases() {
    const [phases, setPhases] = useState([]);
    const [error, setError] = useState(null);

    useEffect(() => {
        let alive = true;
        loadPhases()
            .then((rows) => alive && setPhases(rows))
            .catch((e) => alive && setError(e));
        return () => { alive = false; };
    }, []);

    const byNumber = Object.fromEntries(phases.map((p) => [p.phase_number, p.name]));

    /** Falls back to the number rather than an empty string. A phase the office
     *  retired still has entries pointing at it, and "14" tells you more than
     *  a blank does. */
    const phaseName = (n) => byNumber[n] || `Phase ${n}`;

    return { phases, byNumber, phaseName, error };
}
